import {
	type IMergedSlot,
	type IPuEventChecklistResponse,
	type IPuEventQueueItem,
	PU_EVENT_LABELS,
	PU_EVENT_ORDER,
	type PuEventType,
} from '../interface/pu-events';

// Pure merge of the server checklist with the local capture queue — the
// local-first reconciliation rules for the Election Day Pulse UI:
//   1. Server captured  → authoritative (confirmed), local queue item is stale.
//   2. Local pending/sending → optimistic captured with a "syncing" chip.
//   3. Local failed     → pending + retry affordance (NOT captured).
//   4. Local armed      → still inside the undo grace window → plain pending
//      (counting it as captured would advance "next expected" and hide the
//      undo countdown — the live-confirmed widget bug this rule prevents).
//   5. Neither          → pending.

export const mergeChecklist = (server: IPuEventChecklistResponse | null, queue: IPuEventQueueItem[]): IMergedSlot[] => {
	const serverByType = new Map((server?.slots ?? []).map((slot) => [slot.eventType, slot]));
	const queueByType = new Map(queue.map((item) => [item.request.eventType, item]));

	return PU_EVENT_ORDER.map((eventType: PuEventType): IMergedSlot => {
		const slot = serverByType.get(eventType);
		const local = queueByType.get(eventType);

		if (slot?.captured) {
			return {
				eventType,
				label: PU_EVENT_LABELS[eventType],
				status: 'captured',
				occurredAt: slot.occurredAt ?? null,
				location: slot.location ?? null,
				accuracy: slot.accuracy ?? null,
				mediaUrls: slot.mediaUrls ?? [],
				referenceId: slot.referenceId ?? null,
				gpsDegraded: false,
				confirmed: true,
			};
		}

		if (local && (local.status === 'pending' || local.status === 'sending')) {
			return {
				eventType,
				label: PU_EVENT_LABELS[eventType],
				status: 'syncing',
				occurredAt: local.request.occurredAt,
				location: local.request.location,
				accuracy: local.request.accuracy,
				mediaUrls: local.request.mediaUrls,
				referenceId: local.referenceId,
				gpsDegraded: !!local.gpsDegraded,
				confirmed: false,
			};
		}

		if (local?.status === 'failed') {
			return {
				eventType,
				label: PU_EVENT_LABELS[eventType],
				status: 'failed',
				occurredAt: local.request.occurredAt,
				location: local.request.location,
				accuracy: local.request.accuracy,
				mediaUrls: local.request.mediaUrls,
				referenceId: local.referenceId,
				gpsDegraded: !!local.gpsDegraded,
				confirmed: false,
			};
		}

		return {
			eventType,
			label: PU_EVENT_LABELS[eventType],
			status: 'pending',
			occurredAt: null,
			location: null,
			accuracy: null,
			mediaUrls: [],
			referenceId: null,
			gpsDegraded: false,
			confirmed: false,
		};
	});
};

/** Queue items the server has already confirmed are stale — safe to drop. */
export const staleQueueItems = (server: IPuEventChecklistResponse | null, queue: IPuEventQueueItem[]): IPuEventQueueItem[] => {
	if (!server) return [];
	const capturedTypes = new Set(server.slots.filter((s) => s.captured).map((s) => s.eventType));
	return queue.filter((item) => capturedTypes.has(item.request.eventType));
};
